import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MaskedLines, Reveal, Overline } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, name, type = "text", value, onChange, required = false, textarea = false, testId }) => (
    <label className="block">
        <span className="font-mono-tech text-[10px] tracking-[0.3em] text-white/40">
            {label} {required && <span className="text-[#FFE8B6]/70">*</span>}
        </span>
        {textarea ? (
            <textarea
                data-testid={testId}
                name={name}
                rows={4}
                value={value}
                onChange={onChange}
                className="mt-3 w-full resize-none border-b border-white/20 bg-transparent pb-3 text-base font-light text-white outline-none transition-colors duration-500 focus:border-white/70"
            />
        ) : (
            <input
                data-testid={testId}
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                className="mt-3 w-full border-b border-white/20 bg-transparent pb-3 text-base font-light text-white outline-none transition-colors duration-500 focus:border-white/70"
            />
        )}
    </label>
);

export const Contact = ({ visionPrefill }) => {
    const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", project_type: "", space_location: "", vision: "" });
    const [file, setFile] = useState(null);
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const fileRef = useRef(null);

    useEffect(() => {
        if (visionPrefill) setForm((f) => ({ ...f, vision: visionPrefill }));
    }, [visionPrefill]);

    const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.includes("@")) {
            toast.error("Please add your name and a valid email.");
            return;
        }
        setSending(true);
        try {
            const data = new FormData();
            Object.entries(form).forEach(([k, v]) => data.append(k, v));
            if (file) data.append("file", file);
            await axios.post(`${API}/leads`, data);
            setSent(true);
            toast.success("Thank you — the studio will be in touch.");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="contact" data-testid="contact-section" className="relative border-t border-white/5 py-32 md:py-48">
            <div className="mx-auto grid max-w-[1600px] gap-20 px-6 md:px-12 lg:grid-cols-[1fr_1.1fr] lg:gap-32">
                <div>
                    <Overline>Contact</Overline>
                    <h2 className="mt-10 font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl">
                        <MaskedLines lines={["Tell us about", "your space."]} />
                    </h2>
                    <Reveal className="mt-10 max-w-md">
                        <p className="text-lg font-light leading-relaxed text-white/60">
                            A photograph, a floor plan, a sentence about how it should feel — that's all we need to begin.
                        </p>
                    </Reveal>
                    <Reveal delay={0.15} className="mt-16 space-y-4">
                        <p className="font-mono-tech text-[10px] tracking-[0.35em] text-white/35">STUDIO</p>
                        <p className="text-base font-light text-white/65">Motion Tech Arts — India, working worldwide.</p>
                        <div className="space-y-2 pt-2">
                            <a data-testid="contact-phone-1" href="tel:+917356777997" className="block text-base font-light text-white/65 transition-colors duration-500 hover:text-white">
                                +91 73567 77997
                            </a>
                            <a data-testid="contact-phone-2" href="tel:+918907753777" className="block text-base font-light text-white/65 transition-colors duration-500 hover:text-white">
                                +91 89077 53777
                            </a>
                            <a data-testid="contact-email-link" href="mailto:indialightingstudio@gmail.com" className="block text-base font-light text-white/65 transition-colors duration-500 hover:text-white">
                                indialightingstudio@gmail.com
                            </a>
                        </div>
                    </Reveal>
                </div>
                <div>
                    {sent ? (
                        <div data-testid="contact-success" className="flex h-full min-h-[40vh] flex-col justify-center border border-white/10 p-12">
                            <span className="h-px w-16 bg-[#FFE8B6]/70 shadow-[0_0_12px_2px_rgba(255,214,140,0.35)]" />
                            <h3 className="mt-10 font-display text-3xl font-bold uppercase tracking-tight text-white">The conversation has started.</h3>
                            <p className="mt-6 max-w-md text-base font-light leading-relaxed text-white/60">
                                We've received your note{file ? " and your file" : ""}. The studio will respond within two working days.
                            </p>
                        </div>
                    ) : (
                        <form data-testid="contact-form" onSubmit={submit} className="space-y-10">
                            <div className="grid gap-10 sm:grid-cols-2">
                                <Field label="Name" name="name" value={form.name} onChange={onChange} required testId="contact-name" />
                                <Field label="Company" name="company" value={form.company} onChange={onChange} testId="contact-company" />
                                <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required testId="contact-email" />
                                <Field label="Phone" name="phone" type="tel" value={form.phone} onChange={onChange} testId="contact-phone" />
                                <Field label="Project type" name="project_type" value={form.project_type} onChange={onChange} testId="contact-project-type" />
                                <Field label="Approximate space / location" name="space_location" value={form.space_location} onChange={onChange} testId="contact-location" />
                            </div>
                            <Field label="What are you imagining?" name="vision" value={form.vision} onChange={onChange} textarea testId="contact-vision" />
                            <div>
                                <span className="font-mono-tech text-[10px] tracking-[0.3em] text-white/40">DRAWINGS, RENDERS OR PHOTOGRAPHS</span>
                                <button
                                    type="button"
                                    data-testid="contact-file-upload"
                                    onClick={() => fileRef.current?.click()}
                                    className="mt-3 flex w-full items-center justify-between border border-dashed border-white/20 px-6 py-6 text-left transition-colors duration-500 hover:border-white/50"
                                >
                                    <span className="text-sm font-light text-white/50">
                                        {file ? file.name : "Upload a file — plans, sketches, references"}
                                    </span>
                                    <span className="font-mono-tech text-[10px] tracking-[0.3em] text-white/35">BROWSE</span>
                                </button>
                                <input
                                    ref={fileRef}
                                    type="file"
                                    data-testid="contact-file-input"
                                    className="hidden"
                                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                                />
                            </div>
                            <button
                                type="submit"
                                data-testid="contact-submit"
                                disabled={sending}
                                className="group relative w-full overflow-hidden bg-[#F4F2EF] px-8 py-5 font-mono-tech text-[11px] tracking-[0.35em] uppercase text-[#070708] transition-colors duration-500 disabled:opacity-50"
                            >
                                <span className="relative z-10 transition-colors duration-500 group-hover:text-[#F4F2EF]">
                                    {sending ? "Sending…" : "Start the conversation"}
                                </span>
                                <span className="absolute inset-0 translate-y-full bg-[#070708] transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-y-0" />
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </section>
    );
};
